import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import TokenSelect from "@/components/forms/token.select"
import TokenAmountInput from "@/components/forms/token-amount.input"
import { useFormContext, useWatch } from "react-hook-form"
import NetworkMultiSelect from "@/components/forms/network.multiselect"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PaymentLinkType } from "@/api/payment-links"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { useEffect } from "react"
import { getNetworksForToken } from "@/config"
import { Token } from "@/config/tokens"

export default function PaymentLinkForm() {
  const { setValue } = useFormContext()
  const token: Token = useWatch({ name: "token" })
  const type = useWatch({ name: "type" })

  useEffect(() => {
    setValue("networks", getNetworksForToken(token))
  }, [token, setValue])

  return (
    <div className="flex flex-col gap-4 py-4">
      <FormField
        name="type"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Type</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger
                    className={type ? "" : "text-gray-300"}
                    data-testid="type"
                  >
                    <SelectValue placeholder="Select payment link type" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem
                    value={PaymentLinkType.FIXED_AMOUNT}
                    description="Best for products and services"
                    data-testid="fixed"
                  >
                    Fixed price
                  </SelectItem>
                  <SelectItem
                    value={PaymentLinkType.CUSTOM_AMOUNT}
                    description="Best for tipping, donations, friends or family"
                    data-testid="custom"
                  >
                    Customers choose what to pay
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        name="title"
        render={({ field }) => (
          <FormItem>
            <FormLabel required>Title</FormLabel>
            <FormControl>
              <Input
                placeholder="Enter a title for your payment request"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex flex-row gap-4">
        <FormField
          name="token"
          render={({ field }) => (
            <FormItem className="w-6/12">
              <FormLabel required>Select Token</FormLabel>
              <TokenSelect
                onChange={field.onChange}
                defaultValue={field.value}
              />
            </FormItem>
          )}
        />
        <FormField
          name="amount"
          render={({ field }) => (
            <FormItem className="w-6/12">
              <FormLabel required={type === PaymentLinkType.FIXED_AMOUNT}>
                {type === PaymentLinkType.FIXED_AMOUNT
                  ? "Amount"
                  : "Suggested Amount"}
              </FormLabel>
              <FormControl>
                <TokenAmountInput token={token} field={field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <FormField
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Input placeholder="Add details about the payment" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {type === PaymentLinkType.CUSTOM_AMOUNT && (
        <Collapsible>
          <CollapsibleTrigger data-testid="amount-options">
            Amount Options
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex flex-col gap-4">
              <div className="flex flex-row gap-4 ">
                <FormField
                  name="minAmount"
                  render={({ field }) => (
                    <FormItem className="w-6/12">
                      <FormLabel>Min Amount</FormLabel>
                      <FormControl>
                        <TokenAmountInput token={token} field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name="maxAmount"
                  render={({ field }) => (
                    <FormItem className="w-6/12">
                      <FormLabel>Max Amount</FormLabel>
                      <FormControl>
                        <TokenAmountInput token={token} field={field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <h4 className="text-sm font-semibold">Preset Amounts</h4>
              <div className="flex flex-row gap-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <FormField
                    key={i}
                    name={`presetAmounts[${i}]`}
                    render={({ field }) => (
                      <FormItem className="w-3/12">
                        <FormControl>
                          <Input type="number" placeholder="0" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>
      )}
      <Collapsible>
        <CollapsibleTrigger>Advanced Options</CollapsibleTrigger>
        <CollapsibleContent>
          <FormField
            name="networks"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Networks</FormLabel>
                <NetworkMultiSelect
                  value={field.value}
                  onChange={field.onChange}
                />
                <FormMessage />
              </FormItem>
            )}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  )
}
